import { Location, LocationStrategy, PathLocationStrategy } from '@angular/common'
import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core'
import { MatSidenav } from '@angular/material/sidenav'
import { ActivatedRoute, NavigationStart, Router } from '@angular/router'
import { Observable, of } from 'rxjs'
import { filter, map, startWith, switchMap } from 'rxjs/operators'
import { Constants } from '../../../api/constants/constants'
import { Environments } from '../../../api/constants/environment.enum'
import { Import } from '../../../api/models/import.model'
import { ImportService } from '../../../api/services/import.service'
import { SignalrService } from '../../../api/services/signalr.service'
import { AuthFacade } from '../../auth/auth.facade'
import { CognitoUserInfo } from '../../auth/cognito-user-info'
import { NavigationPaths } from '../../common/components/hierarch-menu/hierarchy-menu.component'
import {
    createHierarchyMenuItem,
    createHierarchyMenuItemFromChild,
    HierarchyMenuItem
} from '../../common/model/hierarchy-menu.model'
import { BreakpointService } from '../../common/services/breakpoint.service'
import { DialogService } from '../../common/services/dialog.service'
import { LoaderService } from '../../common/services/loader.service'
import { PpwLocalStorageService } from '../../common/services/ppwlocalstorage.service'
import { SidenavService } from '../../common/services/sidenav.service'
import { UnsubscribeOnDestroyAdapter } from '../../shared/adapter/unsubscribe-on-destroy-adapter'
import { ProfilePreferencesComponent } from '../components/navigation/profile-preferences/profile-preferences.component'
import { RoleEnum } from '../constants/roles.constant'
import { ImportDialogComponent } from '../import/import-dialog/import-dialog/import-dialog.component'
import { WhatsNewComponent } from '../whats-new/whats-new.component'
import { ImportStatus } from '../../../api/models/import.status.enum'

@Component({
    selector: 'ppw-navigation',
    templateUrl: './navigation.component.html',
    styleUrls: ['./navigation.component.scss'],
    providers: [Location, { provide: LocationStrategy, useClass: PathLocationStrategy }]
})
export class NavigationComponent extends UnsubscribeOnDestroyAdapter implements OnInit, AfterViewInit {
    menuItems?: Array<HierarchyMenuItem>
    isLocal?: boolean
    isTest?: boolean
    isProd?: boolean
    isHandset$: Observable<boolean> = this.breakpointService.observeHandset()

    @ViewChild(MatSidenav) sideNav!: MatSidenav

    currentUser$: Observable<CognitoUserInfo | null> = this.auth.getCurrentUser()
    admin: boolean | undefined
    currentImport$: Observable<Import> | undefined

    private readonly constants: Constants
    private selectedMenuItem?: HierarchyMenuItem
    get frontEndVersion(): string {
        return this.constants.VERSION
    }

    constructor(
        private readonly auth: AuthFacade,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly ppwLocalStorageService: PpwLocalStorageService,
        private readonly sidenavService: SidenavService,
        private readonly loaderService: LoaderService,
        private readonly dialogService: DialogService,
        private readonly breakpointService: BreakpointService,
        private readonly importService: ImportService,
        private readonly signalrService: SignalrService
    ) {
        super()
        this.constants = Constants.getConstantsSingleton()
    }

    ngAfterViewInit(): void {
        this.sidenavService.setSidenav(this.sideNav)
    }

    async ngOnInit(): Promise<void> {
        await this.initialize()
        this.auth.getCurrentUserAccount().subscribe((currentUser) => {
            this.admin = currentUser?.identityRoles.includes(RoleEnum.ADMINISTRATOR)
        })

        this.currentImport$ = this.signalrService.importStatus$.pipe(
            startWith(null),
            switchMap((value: Import | null) => {
                if (value === null) {
                    return this.importService.getLatest()
                } else {
                    return of(value)
                }
            })
        )
    }

    async logout(): Promise<void> {
        await this.auth.signOut()
        // this.autoLogoutService.deactivate()
        this.ppwLocalStorageService.removeAllNonPersistentData()

        if (this.selectedMenuItem) {
            this.selectedMenuItem.isOpen = false
            this.selectedMenuItem = undefined
        }
    }

    showWhatsNew(): void {
        this.dialogService.open(WhatsNewComponent, undefined, '', false)
    }

    showProfilePreferences(): void {
        this.dialogService.open(ProfilePreferencesComponent, { panelClass: 'profile-dialog-container' })
    }

    showImportDialog(): void {
        this.dialogService.open(ImportDialogComponent)
    }

    isBackfillButtonDisabled(status: ImportStatus): boolean {
        return status === ImportStatus.PROCESSING
    }

    private initialize(): void {
        this.refreshMenuStructure()
        this.setEnvironments()
        this.addRouteChangeListeners()
    }

    private addRouteChangeListeners(): void {
        this.subscriptions.add(
            this.router.events
                .pipe(filter((e): e is NavigationStart => e instanceof NavigationStart))
                .subscribe(async (event: NavigationStart) => {
                    if (event.url === NavigationPaths[NavigationPaths.login]) {
                        this.loaderService.startLoading()
                        const combinedPathParams: string = await this.route.queryParamMap
                            .pipe(map((param) => `${param.get('timeCardParams')}___${param.get('timesheetParams')}`))
                            .toPromise()
                        this.ppwLocalStorageService.storeData(
                            this.ppwLocalStorageService.STORAGE_KEY_VIEW_BY_TAG,
                            combinedPathParams.includes('tag')
                        )
                        this.loaderService.stopLoading()
                    }
                })
        )
    }

    private setEnvironments(): void {
        this.isLocal = this.constants.ENVIRONMENT === Environments[Environments.LOCAL]
        this.isTest = this.constants.ENVIRONMENT === Environments[Environments.TEST]
        this.isProd = this.constants.ENVIRONMENT === Environments[Environments.PROD]
    }

    private refreshMenuStructure(): void {
        this.menuItems = new Array<HierarchyMenuItem>()

        const tempArray = new Array<HierarchyMenuItem>()

        const accounts = createHierarchyMenuItem(
            'Accounts',
            [RoleEnum.ADMINISTRATOR],
            NavigationPaths[NavigationPaths.accounts]
        )

        const clients = createHierarchyMenuItem(
            'Clients and Projects',
            RoleEnum.ALL,
            NavigationPaths[NavigationPaths.clients]
        )

        const employees = createHierarchyMenuItem('Employees', RoleEnum.ALL, NavigationPaths[NavigationPaths.employees])

        const timeCardsHeader = createHierarchyMenuItem('Timecards', RoleEnum.ALL)

        const timeCardsEmployeeHeader = createHierarchyMenuItem('By employee', RoleEnum.ALL)
        timeCardsHeader.children.push(timeCardsEmployeeHeader)

        const timecardsEmployeeMonth = createHierarchyMenuItem('By month', RoleEnum.ALL)
        timeCardsEmployeeHeader.children.push(timecardsEmployeeMonth)

        const timecardsEmployeeMonthDayProject = createHierarchyMenuItem(
            'By day - by project',
            RoleEnum.ALL,
            `${NavigationPaths[NavigationPaths.timecards]}/employee/month/day/project`
        )
        timecardsEmployeeMonth.children.push(timecardsEmployeeMonthDayProject)

        const timecardsEmployeeProjectDay = createHierarchyMenuItem(
            'By project - by day',
            RoleEnum.ALL,
            `${NavigationPaths[NavigationPaths.timecards]}/employee/month/project/day`
        )
        timecardsEmployeeMonth.children.push(timecardsEmployeeProjectDay)

        const timeCardsEmployeeProject = createHierarchyMenuItem('By project', RoleEnum.ALL)
        timeCardsEmployeeHeader.children.push(timeCardsEmployeeProject)

        const timecardsEmployeeProjectMonthDay = createHierarchyMenuItem(
            'By month - by day',
            RoleEnum.ALL,
            `${NavigationPaths[NavigationPaths.timecards]}/employee/project/month/day`
        )
        timeCardsEmployeeProject.children.push(timecardsEmployeeProjectMonthDay)

        const timeCardsMonthHeader = createHierarchyMenuItem('By month', RoleEnum.ALL)
        timeCardsHeader.children.push(timeCardsMonthHeader)

        const timecardsMonthEmployee = createHierarchyMenuItem('By employee', RoleEnum.ALL)
        timeCardsMonthHeader.children.push(timecardsMonthEmployee)

        const timecardsMonthEmployeeProjectDay = createHierarchyMenuItem(
            'By project - by day',
            RoleEnum.ALL,
            `${NavigationPaths[NavigationPaths.timecards]}/month/employee/project/day`
        )
        timecardsMonthEmployee.children.push(timecardsMonthEmployeeProjectDay)

        const timecardsMonthProject = createHierarchyMenuItem('By project', RoleEnum.ALL)
        timeCardsMonthHeader.children.push(timecardsMonthProject)

        const timecardsMonthProjectEmployeeDay = createHierarchyMenuItem(
            'By employee - by day',
            RoleEnum.ALL,
            `${NavigationPaths[NavigationPaths.timecards]}/month/project/employee/day`
        )
        timecardsMonthProject.children.push(timecardsMonthProjectEmployeeDay)

        const timeCardsProjectHeader = createHierarchyMenuItem('By project', RoleEnum.ALL)
        timeCardsHeader.children.push(timeCardsProjectHeader)

        const timecardsProjectMonth = createHierarchyMenuItem('By month', RoleEnum.ALL)
        timeCardsProjectHeader.children.push(timecardsProjectMonth)

        const timecardsProjectMonthEmployeeDay = createHierarchyMenuItem(
            'By employee - by day',
            RoleEnum.ALL,
            `${NavigationPaths[NavigationPaths.timecards]}/project/month/employee/day`
        )
        timecardsProjectMonth.children.push(timecardsProjectMonthEmployeeDay)

        const timeCardsTagHeader = createHierarchyMenuItem('By tag', RoleEnum.ALL)
        timeCardsHeader.children.push(timeCardsTagHeader)

        const timecardsTagMonth = createHierarchyMenuItem('By month', RoleEnum.ALL)
        timeCardsTagHeader.children.push(timecardsTagMonth)

        const timecardsTagMonthProject = createHierarchyMenuItem('By project', RoleEnum.ALL)
        timecardsTagMonth.children.push(timecardsTagMonthProject)

        const timecardsTagMonthProjectEmployeeDay = createHierarchyMenuItem(
            'By employee - by day',
            RoleEnum.ALL,
            `${NavigationPaths[NavigationPaths.timecards]}/tag/month/project/employee/day`
        )
        timecardsTagMonthProject.children.push(timecardsTagMonthProjectEmployeeDay)

        const timesheetsHeader = createHierarchyMenuItem(
            'Timesheets',
            RoleEnum.ALL,
            NavigationPaths[NavigationPaths.timesheets]
        )

        const timesheetsbyclientproject = createHierarchyMenuItem(
            'By client',
            RoleEnum.ALL,
            `${NavigationPaths[NavigationPaths.timesheets]}/client`
        )
        timesheetsHeader.children.push(timesheetsbyclientproject)

        const timesheetsbyemployeeproject = createHierarchyMenuItem(
            'By employee',
            RoleEnum.ALL,
            `${NavigationPaths[NavigationPaths.timesheets]}/employee`
        )
        timesheetsHeader.children.push(timesheetsbyemployeeproject)

        const timesheetsbyproject = createHierarchyMenuItem(
            'By project',
            RoleEnum.ALL,
            `${NavigationPaths[NavigationPaths.timesheets]}/project`
        )
        timesheetsHeader.children.push(timesheetsbyproject)

        const timesheetsbytag = createHierarchyMenuItem(
            'By tag',
            RoleEnum.ALL,
            `${NavigationPaths[NavigationPaths.timesheets]}/tag`
        )
        timesheetsHeader.children.push(timesheetsbytag)

        const timesheetsprojectunInvoiced = createHierarchyMenuItem(
            'By project - not invoiced',
            RoleEnum.ALL_BUT_RESTRICTED,
            `${NavigationPaths[NavigationPaths.timesheets]}/not-invoiced`
        )
        timesheetsHeader.children.push(timesheetsprojectunInvoiced)

        const timesheetsstatus = createHierarchyMenuItem(
            'By state',
            RoleEnum.ALL_BUT_RESTRICTED,
            `${NavigationPaths[NavigationPaths.timesheets]}/state`
        )
        timesheetsHeader.children.push(timesheetsstatus)

        tempArray.push(accounts)
        tempArray.push(clients)
        tempArray.push(employees)
        tempArray.push(timeCardsHeader)
        tempArray.push(timesheetsHeader)

        tempArray.forEach((item: HierarchyMenuItem) => {
            const newItem = createHierarchyMenuItemFromChild(item)
            if (this.menuItems !== undefined) {
                this.menuItems.push(newItem)
            }
        })
    }
}
